import { Box, Stack } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AsyncActionStatus } from '../../../actions/common.actionTypes';
import { getCertifiedCourseRegistrations as getCertifiedCourseRegistrationsAction } from '../../../actions/course.actions';
import { cardDimensionsMedia, palette, spaces } from '../../../assets/styles/themes/tokens';
import MasterLayout from '../../../components/AppLayout/MasterLayout';
import PageHeader from '../../../components/PageHeader/PageHeader';
import EmptySearchStateImage from '../../../components/Search/SearchResults/EmptySearchResultsNewUI/EmptySearchStateImage';
import {
	getCertifiedCourseRegistrations,
	getCertifiedCourseRegistrationsStatus,
} from '../../../reducers/courses.selectors';
import { selectUser } from '../../../reducers/user.selectors';
import useAction from '../../../utils/useAction';
import useScreenSize from '../../../utils/useScreenSize';
import Loader from '../Loader';
import MyCertificatesPageItem from './MyCertificatesPageItem';

const MyCertificatesPage = () => {
	const { t } = useTranslation();
	const { isMdScreen, isLgScreen } = useScreenSize();
	const isDesktopScreen = isMdScreen || isLgScreen;
	const user = useSelector(selectUser);

	const fetchCertifiedCourseRegistrations = useAction(getCertifiedCourseRegistrationsAction);
	const certifiedCourseRegistrations = useSelector(getCertifiedCourseRegistrations);
	const certifiedCourseRegistrationsStatus = useSelector(getCertifiedCourseRegistrationsStatus);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (user?.id) {
			fetchCertifiedCourseRegistrations();
		}
	}, [user?.id]);

	const isLoading = [AsyncActionStatus.LOADING, AsyncActionStatus.INITIAL].includes(
		certifiedCourseRegistrationsStatus
	);

	const isEmpty =
		certifiedCourseRegistrationsStatus === AsyncActionStatus.READY &&
		certifiedCourseRegistrations.length === 0;

	return (
		<MasterLayout forceNewNavigation>
			<Box
				sx={{
					paddingX: spaces.md,
					backgroundColor: palette.common.white,
					minHeight: '100%',
				}}
			>
				<Stack
					sx={{
						width: '100%',
						maxWidth: cardDimensionsMedia.desktop.maxWidth,
						marginX: 'auto',
						paddingX: isDesktopScreen ? 0 : spaces.md,
					}}
					direction="column"
				>
					<PageHeader sx={{ color: palette.text.primary }} text={t('My Certificates')} />
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: spaces.xs,
						}}
					>
						{isLoading ? (
							<Loader />
						) : isEmpty ? (
							<EmptySearchStateImage
								title={t('No certificates available')}
								subtitle={t('You have not yet completed any courses.')}
							/>
						) : (
							<Stack data-testid="certificates-list" direction="column" gap={spaces.xs}>
								{certifiedCourseRegistrations.map((courseRegistration) => (
									<MyCertificatesPageItem
										key={courseRegistration.registrationId}
										courseRegistration={courseRegistration}
									/>
								))}
							</Stack>
						)}
					</Box>
				</Stack>
			</Box>
		</MasterLayout>
	);
};

export default MyCertificatesPage;
